import z from 'zod';
import { ConnectResourceType, EntityRecord } from '../../api/api-entities';
import { ConditionMode, ConditionOperator, Field, SchemaFieldType } from '../../schemas/entity';

function evaluateCondition(
  fieldValue: unknown,
  operator: ConditionOperator,
  expectedValue: boolean | string[]
): boolean {
  switch (operator) {
    case ConditionOperator.EQUALS:
      if (typeof expectedValue === 'boolean') {
        return fieldValue === expectedValue;
      }
      if (Array.isArray(expectedValue) && expectedValue.length > 0) {
        return fieldValue === expectedValue[0];
      }
      return false;
    case ConditionOperator.IN:
      return Array.isArray(expectedValue) && expectedValue.includes(String(fieldValue));
    default:
      return false;
  }
}

function evaluateConditional(data: Record<string, unknown>, field: Field): boolean {
  if (!field.conditions || field.conditions.groups.length === 0) {
    return true;
  }

  return field.conditions.groups.some((group) => {
    const results = group.conditions.map((condition) => {
      const fieldValue = data[condition.field];
      return evaluateCondition(fieldValue, condition.operator, condition.value);
    });

    return group.mode === ConditionMode.AND ? results.every(Boolean) : results.some(Boolean);
  });
}

export function createAttributeValidationSchema(entity: EntityRecord): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const [fieldName, fieldConfig] of Object.entries(entity.schema.fields)) {
    let fieldSchema: z.ZodTypeAny;

    switch (fieldConfig.definition.type) {
      case 'string': {
        let schema: z.ZodType = z
          .string({
            error: 'This field is required'
          })
          .min(fieldConfig.definition.minLength)
          .max(fieldConfig.definition.maxLength);

        if (fieldConfig.primary) {
          schema = (schema as z.ZodString).refine((val) => !val.includes('/'), {
            message: 'Primary key cannot contain a slash (/)'
          });
        }

        if (!fieldConfig.definition.required) {
          schema = schema.nullable();
        }

        fieldSchema = z.preprocess((arg) => {
          if (arg === '') {
            return null;
          }

          return arg;
        }, schema);

        break;
      }

      case 'number': {
        let schema: z.ZodType = z.number().min(fieldConfig.definition.minimum).max(fieldConfig.definition.maximum);

        if (!fieldConfig.definition.required) {
          schema = schema.nullable();
        }

        fieldSchema = z.preprocess((arg) => {
          if (arg === '' || arg === null || arg === undefined) {
            return null;
          }

          return arg;
        }, schema);

        break;
      }

      case 'boolean': {
        let schema: z.ZodType = z.boolean({
          error: 'This field is required.'
        });

        if (!fieldConfig.definition.required) {
          schema = schema.nullable();
        }

        fieldSchema = schema;
        break;
      }

      case 'picklist':
        const availableOptions = fieldConfig.definition.options;

        if (fieldConfig.definition.multiple) {
          fieldSchema = z.string().refine(
            (val) => {
              const selectedOptions = val.split(',').map((v) => v.trim());
              return selectedOptions.every((option) => availableOptions.includes(option));
            },
            {
              message: 'One or more invalid picklist options selected.'
            }
          );
        } else {
          fieldSchema = z.string().refine((val) => availableOptions.includes(val), {
            message: 'Invalid picklist option selected.'
          });
        }
        break;

      case 'reference':
        fieldSchema = z.object({
          ref: z.object({
            eid: z.string(),
            iid: z.string()
          })
        });
        break;

      case 'object':
        fieldSchema = z.json().refine(
          (data) => {
            const serialized = JSON.stringify(data);
            return serialized.length < 10000;
          },
          {
            message: 'Object properties exceed maximum size of 10000 bytes.'
          }
        );
        break;

      case 'yaml':
        fieldSchema = z.string().max(10000, {
          message: 'YAML content exceeds maximum size of 10000 characters.'
        });
        break;

      case 'prompt':
        fieldSchema = z.object({
          kind: z.enum(['SSML', 'TEXT']),
          content: z.string()
        });
        break;

      case 'date_time':
        fieldSchema = z.object({
          timeZone: z.string().min(2).max(100),
          date: z.string().max(10),
          time: z.string().max(8)
        });
        break;

      case 'connect_resource': {
        const schema = z.object(
          {
            connect_resource: z.object({
              name: z.string().min(1).max(256),
              arn: z.string().min(1).max(512),
              type: z.enum(ConnectResourceType)
            })
          },
          {
            error: 'A resource must be selected'
          }
        );

        fieldSchema = fieldConfig.definition.required ? schema : schema.nullable();
        break;
      }

      default:
        fieldSchema = z.string();
    }

    if (fieldConfig.conditions && fieldConfig.conditions.groups.length > 0) {
      schemaShape[fieldName] = fieldSchema.optional();
    } else if (fieldConfig.definition.required) {
      schemaShape[fieldName] = fieldSchema;
    } else {
      schemaShape[fieldName] = fieldSchema.optional();
    }
  }

  return z.object(schemaShape).superRefine((data, ctx) => {
    for (const [fieldKey, fieldVal] of Object.entries(entity.schema.fields)) {
      if (!fieldVal.conditions) {
        continue;
      }

      const shouldShow = evaluateConditional(data, fieldVal);

      if (shouldShow) {
        if (fieldVal.definition.required && data[fieldKey] === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: [fieldKey],
            message: `Field '${fieldKey}' is required based on conditions.`
          });
        }
      } else {
        if (data[fieldKey] !== undefined) {
          delete data[fieldKey];
        }
      }
    }
  });
}

export function validatePrimaryField(data: Record<string, Omit<Field, 'inherited'>>): boolean {
  const fields = Object.entries(data).map(([key, value]) => ({ key, ...value }));

  if (fields.length === 0) {
    return true;
  }

  const primaryFields = fields.filter((field) => field.primary);
  if (primaryFields.length > 1) {
    return false;
  }

  const field = primaryFields[0];
  if (!field) {
    return false;
  }

  if (field.definition.type !== SchemaFieldType.STRING) {
    return false;
  }

  if (field.definition.required !== true) {
    return false;
  }

  return true;
}
